import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { db } from "../lib/instant";
import ProductCard from "../components/ProductCard";
import { ChevronRight } from "lucide-react";
import YouTubeSection from "../components/YouTubeSection";

const BENEFITS = [
  { icon: "💪", title: "Builds Strength", desc: "Builds bone and muscle. Repairs damaged cells and supports recovery." },
  { icon: "🩸", title: "Blood Sugar Balance", desc: "Regulates blood sugar — ideal for diabetic patients." },
  { icon: "❤️", title: "Heart Health", desc: "Reduces bad cholesterol and lowers risk of heart disease." },
  { icon: "🛡️", title: "Immune Booster", desc: "Antioxidants and minerals that strengthen your immune system." },
  { icon: "✨", title: "Skin & Anti-Aging", desc: "Reduces premature aging and tackles skin problems from within." },
  { icon: "⚖️", title: "Weight Management", desc: "High fibre and protein promotes satiety and healthy weight." },
];

export default function Home({ theme }) {
  const primaryColor   = theme?.primaryColor   || "#6b2118";
  const accentColor    = theme?.accentColor    || "#c9a84c";
  const secondaryColor = theme?.secondaryColor || "#2e7d32";
  const [activeBanner, setActiveBanner] = useState(0);

  useEffect(() => {
    if (banners.length <= 1) return;
    const t = setInterval(() => setActiveBanner(i => (i + 1) % banners.length), 4000);
    return () => clearInterval(t);
  // eslint-disable-next-line
  }, []);

  const { data } = db.useQuery({ products: { $: { where: { active: true } } }, banners: { $: { where: { active: true } } }, settings: {} });
  const products = (data?.products || []).slice(0, 4);
  const settings = data?.settings?.[0] || {};
  const banners  = [...(data?.banners  || [])].sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div style={{ paddingTop: 64 }}>

      {/* ── HERO ── */}
      <section style={{
        minHeight: "calc(100svh - 64px)",
        background: `linear-gradient(150deg, #1a0805 0%, ${primaryColor} 40%, #2c1208 100%)`,
        display: "flex", alignItems: "center", justifyContent: "center",
        textAlign: "center", position: "relative", overflow: "hidden",
        padding: "48px 24px",
      }}>

        {/* ── Background pattern ── */}
        <svg style={{ position:"absolute", inset:0, width:"100%", height:"100%", opacity:0.07, pointerEvents:"none" }} xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="grain-pattern" x="0" y="0" width="80" height="80" patternUnits="userSpaceOnUse">
              {/* Wheat stalk */}
              <line x1="40" y1="70" x2="40" y2="10" stroke={accentColor} strokeWidth="1"/>
              <ellipse cx="40" cy="18" rx="5" ry="8" fill="none" stroke={accentColor} strokeWidth="1" transform="rotate(-20 40 18)"/>
              <ellipse cx="40" cy="18" rx="5" ry="8" fill="none" stroke={accentColor} strokeWidth="1" transform="rotate(20 40 18)"/>
              <ellipse cx="40" cy="30" rx="4" ry="7" fill="none" stroke={accentColor} strokeWidth="1" transform="rotate(-25 40 30)"/>
              <ellipse cx="40" cy="30" rx="4" ry="7" fill="none" stroke={accentColor} strokeWidth="1" transform="rotate(25 40 30)"/>
              <ellipse cx="40" cy="42" rx="3" ry="6" fill="none" stroke={accentColor} strokeWidth="1" transform="rotate(-20 40 42)"/>
              <ellipse cx="40" cy="42" rx="3" ry="6" fill="none" stroke={accentColor} strokeWidth="1" transform="rotate(20 40 42)"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grain-pattern)"/>
        </svg>

        {/* ── Radial glow centre ── */}
        <div style={{
          position:"absolute", top:"50%", left:"50%",
          transform:"translate(-50%,-50%)",
          width:"70vw", maxWidth:700, height:"70vw", maxHeight:700,
          borderRadius:"50%",
          background:`radial-gradient(circle, ${primaryColor}88 0%, transparent 70%)`,
          pointerEvents:"none",
        }}/>

        {/* ── Concentric rings ── */}
        {[280, 420, 560, 700].map((size, i) => (
          <div key={i} style={{
            position:"absolute", top:"50%", left:"50%",
            transform:"translate(-50%,-50%)",
            width:size, height:size,
            borderRadius:"50%",
            border:`1px solid ${accentColor}${i === 0 ? "30" : i === 1 ? "22" : i === 2 ? "14" : "08"}`,
            animation:`ring-pulse 6s ease-in-out ${i * 1.2}s infinite`,
            pointerEvents:"none",
          }}/>
        ))}

        {/* ── Corner leaf accents ── */}
        <svg style={{ position:"absolute", top:0, left:0, width:160, height:160, opacity:0.12, pointerEvents:"none" }} viewBox="0 0 160 160">
          <path d="M0,0 Q80,0 80,80 Q40,40 0,0Z" fill={accentColor}/>
          <path d="M0,0 Q60,20 40,80 Q20,40 0,0Z" fill={accentColor} opacity="0.5"/>
        </svg>
        <svg style={{ position:"absolute", bottom:0, right:0, width:160, height:160, opacity:0.12, pointerEvents:"none" }} viewBox="0 0 160 160">
          <path d="M160,160 Q80,160 80,80 Q120,120 160,160Z" fill={accentColor}/>
          <path d="M160,160 Q100,140 120,80 Q140,120 160,160Z" fill={accentColor} opacity="0.5"/>
        </svg>

        {/* ── Top-right small dots ── */}
        <svg style={{ position:"absolute", top:40, right:40, opacity:0.15, pointerEvents:"none" }} width="80" height="80" viewBox="0 0 80 80">
          {[0,1,2,3].map(row => [0,1,2,3].map(col => (
            <circle key={`${row}-${col}`} cx={col*20+10} cy={row*20+10} r="2.5" fill={accentColor}/>
          )))}
        </svg>
        {/* ── Bottom-left small dots ── */}
        <svg style={{ position:"absolute", bottom:40, left:40, opacity:0.15, pointerEvents:"none" }} width="80" height="80" viewBox="0 0 80 80">
          {[0,1,2,3].map(row => [0,1,2,3].map(col => (
            <circle key={`${row}-${col}`} cx={col*20+10} cy={row*20+10} r="2.5" fill={accentColor}/>
          )))}
        </svg>

        {/* ── Hero content ── */}
        <div style={{ position:"relative", zIndex:2, maxWidth:680, width:"100%" }}>

          {/* Badge */}
          <div style={{
            display:"inline-flex", alignItems:"center", gap:8,
            background:`rgba(201,168,76,0.12)`, border:`1px solid ${accentColor}50`,
            color:accentColor, fontSize:"0.68rem", letterSpacing:3,
            textTransform:"uppercase", padding:"6px 18px", borderRadius:40, marginBottom:24,
          }}>
            <span style={{ width:5, height:5, borderRadius:"50%", background:accentColor, display:"inline-block" }}/>
            No Preservatives · 100% Natural
          </div>

          {/* Headline */}
          <h1 style={{
            fontFamily:"Georgia, serif",
            fontSize:"clamp(2.6rem, 7vw, 5rem)",
            lineHeight:1.05, margin:"0 0 12px",
          }}>
            <span style={{ color:"#fff" }}>ACHU </span>
            <span style={{ color:accentColor }}>Health Mix</span>
          </h1>

          {/* Subheadline */}
          <p style={{
            fontSize:"clamp(1rem, 2.5vw, 1.2rem)",
            color:"rgba(255,255,255,0.6)",
            marginBottom:12, lineHeight:1.6,
            fontFamily:"cursive",
          }}>Specially Hand Made for You!</p>

          <p style={{
            fontSize:"clamp(0.85rem, 1.8vw, 0.95rem)",
            color:"rgba(255,255,255,0.5)",
            marginBottom:28, lineHeight:1.7,
            maxWidth:520, margin:"0 auto 28px",
          }}>
            A blend of <strong style={{ color:"rgba(255,255,255,0.8)" }}>27 grains, millets & dry fruits</strong>.
            Zero sugar · Zero additives · No preservatives. Good for all ages.
          </p>

          {/* Pills */}
          <div style={{ display:"flex", flexWrap:"wrap", gap:8, justifyContent:"center", marginBottom:36 }}>
            {["🌾 27 Multi Grains","🚫 Zero Sugar","🌿 Zero Additives","👨‍👩‍👧 All Ages"].map(t => (
              <span key={t} style={{
                background:"rgba(255,255,255,0.07)",
                border:"1px solid rgba(255,255,255,0.15)",
                color:"rgba(255,255,255,0.85)",
                padding:"6px 14px", borderRadius:40, fontSize:"0.78rem",
              }}>{t}</span>
            ))}
          </div>

          {/* CTAs */}
          <div style={{ display:"flex", gap:14, justifyContent:"center", flexWrap:"wrap" }}>
            <Link to="/shop" style={{
              background:accentColor, color:"#2c1810",
              padding:"14px 36px", borderRadius:40,
              fontWeight:800, fontSize:"0.92rem", letterSpacing:1,
              textDecoration:"none", textTransform:"uppercase",
              boxShadow:`0 6px 28px rgba(201,168,76,0.4)`,
            }}>Shop Now →</Link>
            <Link to="/about" style={{
              border:"2px solid rgba(255,255,255,0.3)", color:"#fff",
              padding:"14px 28px", borderRadius:40,
              fontWeight:700, fontSize:"0.92rem",
              textDecoration:"none", textTransform:"uppercase",
            }}>Learn More</Link>
          </div>
        </div>

        <style>{`
          @keyframes ring-pulse { 0%,100%{opacity:0.4;transform:translate(-50%,-50%) scale(1)} 50%{opacity:1;transform:translate(-50%,-50%) scale(1.03)} }
        `}</style>
      </section>

      {/* ── STATS ── */}
      <div style={{ background:primaryColor, display:"grid", gridTemplateColumns:"repeat(4,1fr)" }}>
        {[["27","Multi Grains"],["100%","Natural"],["0","Preservatives"],["All","Age Groups"]].map(([n,l]) => (
          <div key={l} style={{ padding:"22px 12px", textAlign:"center", borderRight:"1px solid rgba(255,255,255,0.1)" }}>
            <div style={{ fontFamily:"Georgia, serif", fontSize:"1.8rem", color:accentColor }}>{n}</div>
            <div style={{ fontSize:"0.68rem", color:"rgba(255,255,255,0.65)", letterSpacing:2, textTransform:"uppercase", marginTop:3 }}>{l}</div>
          </div>
        ))}
      </div>

      {/* ── BANNERS ── */}
      {banners.length > 0 && (
        <section style={{ background: "#fdf6e3", padding: "24px 5vw" }}>
          <div style={{ maxWidth: 1200, margin: "0 auto", position: "relative" }}>
            {/* Slide */}
            {banners.map((banner, i) => (
              <div key={banner.id} style={{
                display: i === activeBanner ? "block" : "none",
              }}>
                <Link to={banner.link || "/shop"} style={{ textDecoration: "none", display: "block" }}>
                  <div style={{
                    borderRadius: 16, overflow: "hidden",
                    background: banner.bgColor || primaryColor,
                    minHeight: 120,
                    position: "relative",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
                  }}>
                    {banner.imageUrl ? (
                      <img
                        src={banner.imageUrl}
                        alt={banner.title}
                        style={{ width: "100%", maxHeight: 280, objectFit: "cover", display: "block" }}
                      />
                    ) : (
                      <div style={{ padding: "32px 40px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
                        <div>
                          <h2 style={{ fontFamily: "Georgia, serif", fontSize: "clamp(1.4rem, 3vw, 2rem)", color: "#fff", margin: "0 0 8px" }}>{banner.title}</h2>
                          {banner.subtitle && <p style={{ color: "rgba(255,255,255,0.8)", margin: "0 0 16px", fontSize: "0.95rem" }}>{banner.subtitle}</p>}
                          <div style={{ display: "inline-block", background: accentColor, color: "#2c1810", padding: "10px 24px", borderRadius: 40, fontWeight: 800, fontSize: "0.88rem" }}>
                            Shop Now →
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </Link>
              </div>
            ))}

            {/* Dots — only if multiple banners */}
            {banners.length > 1 && (
              <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 14 }}>
                {banners.map((_, i) => (
                  <button key={i} onClick={() => setActiveBanner(i)} style={{
                    width: i === activeBanner ? 24 : 8,
                    height: 8, borderRadius: 4,
                    background: i === activeBanner ? primaryColor : "#ccc",
                    border: "none", cursor: "pointer", padding: 0,
                    transition: "all 0.3s",
                  }} />
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* ── FEATURED PRODUCTS ── */}
      <section style={{ padding:"72px 5vw", background:"#fdf6e3" }}>
        <div style={{ maxWidth:1200, margin:"0 auto" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-end", marginBottom:40, flexWrap:"wrap", gap:16 }}>
            <div>
              <div style={{ fontSize:"0.68rem", letterSpacing:4, textTransform:"uppercase", color:secondaryColor, fontWeight:700, marginBottom:6 }}>Our Products</div>
              <h2 style={{ fontFamily:"Georgia, serif", fontSize:"clamp(1.6rem,3vw,2.4rem)", color:"#2c1810", lineHeight:1.1 }}>
                Choose Your <span style={{ color:secondaryColor }}>Pack</span>
              </h2>
            </div>
            <Link to="/shop" style={{ display:"flex", alignItems:"center", gap:6, color:primaryColor, fontWeight:700, textDecoration:"none", fontSize:"0.9rem" }}>
              View All <ChevronRight size={18}/>
            </Link>
          </div>
          {products.length === 0 ? (
            <div style={{ textAlign:"center", padding:"60px 0" }}>
              <div style={{ fontSize:"3rem", marginBottom:12 }}>🌾</div>
              <p style={{ color:"#aaa" }}>Products coming soon. Add from Admin Panel.</p>
            </div>
          ) : (
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(240px,1fr))", gap:24 }}>
              {products.map(p => <ProductCard key={p.id} product={p} theme={theme}/>)}
            </div>
          )}
        </div>
      </section>

      {/* ── BENEFITS ── */}
      <section style={{ padding:"72px 5vw", background:"#fff" }}>
        <div style={{ maxWidth:1200, margin:"0 auto" }}>
          <div style={{ textAlign:"center", marginBottom:48 }}>
            <div style={{ fontSize:"0.68rem", letterSpacing:4, textTransform:"uppercase", color:secondaryColor, fontWeight:700, marginBottom:8 }}>Why Choose Us</div>
            <h2 style={{ fontFamily:"Georgia, serif", fontSize:"clamp(1.6rem,3vw,2.4rem)", color:"#2c1810" }}>
              Powerful <span style={{ color:secondaryColor }}>Benefits</span>
            </h2>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))", gap:20 }}>
            {BENEFITS.map(b => (
              <div key={b.title} style={{ background:"#fdf6e3", borderRadius:16, padding:"24px 20px", borderLeft:`4px solid ${secondaryColor}`, boxShadow:"0 2px 8px rgba(0,0,0,0.04)" }}>
                <div style={{ fontSize:"1.8rem", marginBottom:10 }}>{b.icon}</div>
                <h3 style={{ fontSize:"0.95rem", fontWeight:700, color:primaryColor, marginBottom:6 }}>{b.title}</h3>
                <p style={{ fontSize:"0.82rem", color:"#666", lineHeight:1.7 }}>{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── YOUTUBE VIDEOS ── */}
      <YouTubeSection theme={theme}/>

      {/* ── HOW TO USE ── */}
      <section style={{ padding:"72px 5vw", background:primaryColor }}>
        <div style={{ maxWidth:900, margin:"0 auto", textAlign:"center" }}>
          <div style={{ fontSize:"0.68rem", letterSpacing:4, textTransform:"uppercase", color:accentColor, fontWeight:700, marginBottom:8 }}>Simple & Delicious</div>
          <h2 style={{ fontFamily:"Georgia, serif", fontSize:"clamp(1.6rem,3vw,2.4rem)", color:"#fff", marginBottom:48 }}>
            How to <span style={{ color:accentColor }}>Prepare</span>
          </h2>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))", gap:28 }}>
            {[
              { n:"1", t:"Measure",  d:"Take 2–3 tbsp of ACHU Health Mix into a cup." },
              { n:"2", t:"Mix",      d:"Add warm water or milk, stir into a smooth paste." },
              { n:"3", t:"Sweeten",  d:"Add jaggery, honey or sweetener to taste." },
              { n:"4", t:"Enjoy!",   d:"Sip warm or chilled daily for best results." },
            ].map(s => (
              <div key={s.n}>
                <div style={{ width:60, height:60, borderRadius:"50%", background:"rgba(255,255,255,0.1)", border:`2px solid ${accentColor}`, display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 14px", fontFamily:"Georgia, serif", fontSize:"1.4rem", color:accentColor }}>{s.n}</div>
                <h3 style={{ color:"#fff", fontWeight:700, marginBottom:6, fontSize:"0.95rem" }}>{s.t}</h3>
                <p style={{ color:"rgba(255,255,255,0.6)", fontSize:"0.82rem", lineHeight:1.6 }}>{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section style={{ padding:"72px 5vw", background:secondaryColor, textAlign:"center" }}>
        <h2 style={{ fontFamily:"Georgia, serif", fontSize:"clamp(1.6rem,3vw,2.4rem)", color:"#fff", marginBottom:14 }}>Start Your Health Journey Today</h2>
        <p style={{ color:"rgba(255,255,255,0.75)", marginBottom:32, fontSize:"0.95rem" }}>Join thousands of families nourishing themselves every morning.</p>
        <Link to="/shop" style={{ background:"#fff", color:secondaryColor, padding:"14px 40px", borderRadius:40, fontWeight:800, fontSize:"1rem", textDecoration:"none", display:"inline-block", boxShadow:"0 6px 24px rgba(0,0,0,0.15)" }}>
          Order Now →
        </Link>
      </section>

    </div>
  );
}
